import React, { useState, useCallback, useEffect, useRef } from 'react';
import { GoogleMap, useLoadScript, OverlayView } from '@react-google-maps/api';
import styled, { keyframes, css } from 'styled-components';

const LIBRARIES = ['places'];

// Dark theme — matches the glassmorphism design with deep navy tones.
const DARK_MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#0f172a' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#64748b' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#0f172a' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#1e3a5f' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#4a6f8a' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#162032' }] },
  { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#1a2740' }] },
  { featureType: 'landscape.man_made', elementType: 'geometry', stylers: [{ color: '#152238' }] },
  { featureType: 'administrative.country', elementType: 'geometry.stroke', stylers: [{ color: '#334155' }, { weight: 0.8 }] },
  { featureType: 'administrative.country', elementType: 'labels.text.fill', stylers: [{ color: '#475569' }] },
  { featureType: 'administrative.province', elementType: 'geometry.stroke', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
];

const MAP_OPTIONS = {
  styles: DARK_MAP_STYLES,
  disableDefaultUI: true,
  gestureHandling: 'greedy',
  minZoom: 1,
  maxZoom: 14,
  backgroundColor: '#0f172a',
};

const DEFAULT_CENTER = { lat: 25, lng: 10 };
const DEFAULT_ZOOM = 2;

// ===== ANIMATIONS =====
const pulseRing = keyframes`
  0%   { transform: scale(0.5); opacity: 0.8; }
  80%, 100% { transform: scale(2.4); opacity: 0; }
`;

// ===== MARKER STYLED COMPONENTS =====

// Active pin — blue solid dot with white ring and pulse
const ActiveMarkerDot = styled.div`
  width: 14px;
  height: 14px;
  background: #4285F4;
  border-radius: 50%;
  border: 2px solid #ffffff;
  box-shadow: 0 2px 8px rgba(66,133,244,0.6), 0 0 0 3px rgba(66,133,244,0.2);
  cursor: pointer;
  position: relative;
  transform: translate(-50%, -50%);

  &::before {
    content: '';
    position: absolute;
    inset: -7px;
    border-radius: 50%;
    background: rgba(66, 133, 244, 0.2);
    animation: ${props => props.$pulse ? css`${pulseRing} 1.8s ease-out infinite` : 'none'};
  }
`;

// Inactive pin — small white dot with subtle blue shadow
const InactiveMarkerDot = styled.div`
  width: 10px;
  height: 10px;
  background: #ffffff;
  border-radius: 50%;
  border: 2px solid rgba(66, 133, 244, 0.6);
  box-shadow: 0 2px 6px rgba(66,133,244,0.3);
  cursor: pointer;
  transition: all 0.15s;
  transform: translate(-50%, -50%);

  &:hover {
    background: #4285F4;
    border-color: #ffffff;
    box-shadow: 0 2px 12px rgba(66,133,244,0.6);
    transform: translate(-50%, -50%) scale(1.4);
  }
`;

// Glass tooltip — matches G.glass.medium + G.blur.lg + G.border.medium
const Tooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 12px);
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  padding: 0.5rem 0.75rem;
  min-width: 160px;
  max-width: 220px;
  pointer-events: none;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  z-index: 10;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: rgba(255, 255, 255, 0.28);
  }
`;

const TooltipTitle = styled.p`
  font-size: 0.6875rem;
  font-weight: 700;
  color: white;
  line-height: 1.3;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TooltipMeta = styled.div`
  display: flex;
  gap: 0.5rem;
  font-size: 0.625rem;
  color: rgba(255, 255, 255, 0.7);

  span { white-space: nowrap; }
`;

// "X more destinations" floating indicator
const MorePinsBadge = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 999;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  background: rgba(10, 16, 36, 0.88);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 9999px;
  color: #ffffff;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
  transition: background 0.2s, box-shadow 0.2s;
  white-space: nowrap;

  &:hover {
    background: rgba(66, 133, 244, 0.25);
    border-color: rgba(66, 133, 244, 0.5);
  }

  &:active {
    transform: scale(0.96);
  }

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

// Loading/error state — glass panel matching the page
const LoadingOverlay = styled.div`
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
  font-weight: 500;
`;

// ===== MARKER COMPONENT =====
function ExploreMarker({ itinerary, isActive, isHighlighted, onClick }) {
  const [hovered, setHovered] = useState(false);
  if (!itinerary.mapPosition) return null;

  const showBright = isActive || isHighlighted;
  const showTooltip = isActive || isHighlighted || hovered;

  return (
    <OverlayView
      position={{ lat: itinerary.mapPosition.lat, lng: itinerary.mapPosition.lng }}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      <div
        style={{ position: 'relative' }}
        onClick={() => onClick(itinerary.id)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {showBright
          ? <ActiveMarkerDot $pulse={isActive} />
          : <InactiveMarkerDot />
        }
        {showTooltip && (
          <Tooltip>
            <TooltipTitle>{itinerary.title}</TooltipTitle>
            <TooltipMeta>
              {itinerary.cost != null && <span>${itinerary.cost.toLocaleString()}</span>}
              {itinerary.days != null && <span>{itinerary.days} days</span>}
            </TooltipMeta>
          </Tooltip>
        )}
      </div>
    </OverlayView>
  );
}

// ===== MAIN COMPONENT =====
export default function ExploreGoogleMap({ itineraries = [], selectedId, hoveredId, onSelectItinerary, visible }) {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: LIBRARIES,
  });

  const [map, setMap] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [hiddenCount, setHiddenCount] = useState(0);
  const containerRef = useRef(null);
  const hasFittedRef = useRef(false);
  const settledRef = useRef(false);      // true once layout has fully settled
  const userSelectedRef = useRef(false); // true after user clicks a pin (not auto-select)
  const prevSelectedIdRef = useRef(selectedId);
  const itinerariesRef = useRef(itineraries);
  itinerariesRef.current = itineraries;

  // Helper: fit all pins into view
  const fitPins = useCallback((mapInstance, pins) => {
    if (!mapInstance || !window.google?.maps || pins.length === 0) return;
    window.google.maps.event.trigger(mapInstance, 'resize');
    if (pins.length === 1) {
      mapInstance.setCenter({ lat: pins[0].mapPosition.lat, lng: pins[0].mapPosition.lng });
      mapInstance.setZoom(5);
    } else {
      const bounds = new window.google.maps.LatLngBounds();
      pins.forEach(it => bounds.extend({ lat: it.mapPosition.lat, lng: it.mapPosition.lng }));
      mapInstance.fitBounds(bounds, 80);
    }
  }, []);

  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
    // Once tiles are fully loaded (container dimensions are final), do a fit
    const tilesListener = mapInstance.addListener('tilesloaded', () => {
      window.google.maps.event.removeListener(tilesListener);
      const pins = itinerariesRef.current.filter(it => it.mapPosition);
      if (pins.length > 0) {
        // Small delay to let the flex layout finalize
        setTimeout(() => {
          window.google.maps.event.trigger(mapInstance, 'resize');
          if (pins.length === 1) {
            mapInstance.setCenter({ lat: pins[0].mapPosition.lat, lng: pins[0].mapPosition.lng });
            mapInstance.setZoom(5);
          } else {
            const bounds = new window.google.maps.LatLngBounds();
            pins.forEach(it => bounds.extend({ lat: it.mapPosition.lat, lng: it.mapPosition.lng }));
            mapInstance.fitBounds(bounds, 80);
          }
          settledRef.current = true;
        }, 300);
      }
    });
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
    setMapReady(false);
    hasFittedRef.current = false;
  }, []);

  // Mark map ready on first idle, then do initial fitBounds.
  useEffect(() => {
    if (!map || !window.google?.maps) return;
    setMapReady(false);
    hasFittedRef.current = false;
    settledRef.current = false;
    const listener = map.addListener('idle', () => {
      setMapReady(true);
      if (!hasFittedRef.current) {
        const pins = itinerariesRef.current.filter(it => it.mapPosition);
        if (pins.length > 0) {
          hasFittedRef.current = true;
          // Delay so desktop flex layout can settle, then fit multiple times as safety net
          setTimeout(() => {
            fitPins(map, pins);
            setTimeout(() => {
              fitPins(map, pins);
              setTimeout(() => {
                fitPins(map, pins);
                settledRef.current = true;
              }, 600);
            }, 400);
          }, 200);
        }
      }
    });
    return () => window.google.maps.event.removeListener(listener);
  }, [map, fitPins]);

  // When itineraries arrive (async) and we haven't fitted yet, fit now.
  useEffect(() => {
    if (!map || !mapReady || !window.google?.maps || hasFittedRef.current) return;
    const pins = itineraries.filter(it => it.mapPosition);
    if (pins.length === 0) return;
    hasFittedRef.current = true;
    // Staggered fits to handle desktop flex layout settling
    const t1 = setTimeout(() => fitPins(map, pins), 100);
    const t2 = setTimeout(() => fitPins(map, pins), 500);
    const t3 = setTimeout(() => {
      fitPins(map, pins);
      settledRef.current = true;
    }, 1000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [map, mapReady, itineraries, fitPins]);

  // When mobile map panel toggles visible, resize then re-fit.
  useEffect(() => {
    if (!visible || !map || !mapReady || !window.google?.maps) return;
    const pins = itineraries.filter(it => it.mapPosition);
    if (pins.length === 0) return;
    const timer = setTimeout(() => fitPins(map, pins), 150);
    return () => clearTimeout(timer);
  }, [visible, map, mapReady, itineraries, fitPins]);

  // Use ResizeObserver to keep map dimensions in sync with container.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !map || !window.google?.maps) return;
    let prevWidth = el.offsetWidth;
    let prevHeight = el.offsetHeight;
    let debounceTimer = null;

    const observer = new ResizeObserver(() => {
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      if (w === prevWidth && h === prevHeight) return;
      prevWidth = w;
      prevHeight = h;
      if (w > 0 && h > 0) {
        window.google.maps.event.trigger(map, 'resize');
        // Re-fit while layout is still settling — debounce to avoid thrashing
        if (!settledRef.current) {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            const pins = itinerariesRef.current.filter(it => it.mapPosition);
            if (pins.length > 0) fitPins(map, pins);
          }, 100);
        }
      }
    });
    observer.observe(el);
    return () => { observer.disconnect(); clearTimeout(debounceTimer); };
  }, [map, fitPins]);

  // Track how many pins are outside the current viewport
  useEffect(() => {
    if (!map || !mapReady || !window.google?.maps) return;

    const updateHiddenCount = () => {
      const bounds = map.getBounds();
      if (!bounds) { setHiddenCount(0); return; }
      const pins = itineraries.filter(it => it.mapPosition);
      const outside = pins.filter(it => !bounds.contains({ lat: it.mapPosition.lat, lng: it.mapPosition.lng }));
      setHiddenCount(outside.length);
    };

    updateHiddenCount();
    const listener = map.addListener('idle', updateHiddenCount);
    return () => window.google.maps.event.removeListener(listener);
  }, [map, mapReady, itineraries]);

  const fitAllPins = useCallback(() => {
    if (!map || !window.google?.maps) return;
    const pins = itineraries.filter(it => it.mapPosition);
    if (pins.length === 0) return;
    fitPins(map, pins);
  }, [map, itineraries, fitPins]);

  // Track whether selectedId was changed by user interaction (pin/card click)
  // vs. the automatic first-selection on mount. We skip panTo for auto-select
  // so that fitBounds can show all pins first.
  useEffect(() => {
    if (prevSelectedIdRef.current != null && selectedId !== prevSelectedIdRef.current) {
      // selectedId changed after initial set → user clicked something
      userSelectedRef.current = true;
    }
    prevSelectedIdRef.current = selectedId;
  }, [selectedId]);

  // Pan to selected pin when user explicitly clicks a pin/card
  useEffect(() => {
    if (!map || !mapReady || !selectedId || !window.google?.maps) return;
    // Skip auto-pan for the initial auto-select; let fitBounds handle the view
    if (!userSelectedRef.current) return;
    const pin = itineraries.find(it => it.id === selectedId);
    if (pin?.mapPosition) {
      // Ensure map knows its real container size before panning
      window.google.maps.event.trigger(map, 'resize');
      map.panTo({ lat: pin.mapPosition.lat, lng: pin.mapPosition.lng });
      if (map.getZoom() < 4) {
        map.setZoom(5);
      }
    }
  }, [map, mapReady, selectedId, itineraries]);

  if (loadError) {
    return <LoadingOverlay>Map unavailable</LoadingOverlay>;
  }

  if (!isLoaded) {
    return <LoadingOverlay>Loading map…</LoadingOverlay>;
  }

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', position: 'relative' }}>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        options={MAP_OPTIONS}
        onLoad={onLoad}
        onUnmount={onUnmount}
      >
        {mapReady && itineraries.map(it =>
          it.mapPosition ? (
            <ExploreMarker
              key={it.id}
              itinerary={it}
              isActive={selectedId === it.id}
              isHighlighted={hoveredId === it.id}
              onClick={onSelectItinerary}
            />
          ) : null
        )}
      </GoogleMap>

      {hiddenCount > 0 && (
        <MorePinsBadge onClick={fitAllPins}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          {hiddenCount} more {hiddenCount === 1 ? 'destination' : 'destinations'} — Show all
        </MorePinsBadge>
      )}
    </div>
  );
}
